import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, Search } from 'lucide-react';
import { SiteFooter } from './SiteFooter';
import { CoverImage } from './CoverImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

export const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [tag, setTag] = useState('');
  const [tags, setTags] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/blog/tags`)
      .then((r) => r.json())
      .then((d) => setTags(d.tags || []))
      .catch(() => { /* tag row simply hides */ });
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (q.trim()) params.set('q', q.trim());
        if (tag) params.set('tag', tag);
        const res = await fetch(`${API_URL}/api/blog/posts?${params.toString()}`);
        if (!res.ok) throw new Error('Failed to load posts');
        const data = await res.json();
        setPosts(data.posts || []);
        setError('');
      } catch (err) {
        setError(err.message || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    }, q ? 350 : 0);
    return () => clearTimeout(timer);
  }, [q, tag]);

  const isFiltering = q.trim() || tag;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] text-[#e8dcc8]">
      {/* Top bar */}
      <div className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-[#d4c4a8]/60 hover:text-[#e89b47] transition-colors font-body text-sm"
          data-testid="blog-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>
        <a
          href="https://calendly.com/marketingtarot/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 h-10 px-5 text-sm bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:from-[#e89b47]/90 hover:to-[#d4883a]/90 font-display tracking-widest shadow-lg shadow-[#e89b47]/25 rounded-xl transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#e89b47]/30"
          data-testid="blog-book-call-btn"
        >
          Book a Clarity Call
        </a>
      </div>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-12 text-center">
        <p className="font-display text-sm tracking-[0.3em] text-[#e89b47] mb-6 uppercase">
          Blog
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#e8dcc8] leading-tight mb-6" data-testid="blog-headline">
          The Marketing Tarot <span className="text-[#e89b47]">Blog</span>
        </h1>
        <h2 className="font-display text-lg sm:text-xl text-[#d4c4a8]/80 mb-6" data-testid="blog-subheadline">
          Insights &amp; Strategies for Modern Marketing
        </h2>
        <p className="font-body text-base text-[#d4c4a8]/70 max-w-2xl mx-auto">
          Fuel your marketing and branding fire with our insightful blog. Dive into actionable strategies,
          industry trends, and thought-provoking ideas &ndash; all in one place.
        </p>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-[#e89b47]/30 to-transparent" />
      </div>

      {/* Search & filters */}
      <section className="max-w-4xl mx-auto px-6 pt-10">
        <div className="relative max-w-md mx-auto mb-5">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#d4c4a8]/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search posts…"
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-[#1e2a4a]/40 border border-[#d4c4a8]/15 text-[#e8dcc8] placeholder-[#d4c4a8]/30 font-body text-sm focus:outline-none focus:border-[#e89b47]/50"
            data-testid="blog-search-input"
          />
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2" data-testid="blog-tag-filters">
            <button
              type="button"
              onClick={() => setTag('')}
              className={`px-4 h-8 rounded-full text-xs font-display tracking-widest uppercase border transition-colors ${
                tag === '' ? 'bg-[#e89b47] text-[#0a1628] border-[#e89b47]' : 'text-[#d4c4a8] border-[#d4c4a8]/20 hover:border-[#e89b47]'
              }`}
              data-testid="blog-tag-all"
            >
              All
            </button>
            {tags.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTag(t === tag ? '' : t)}
                className={`px-4 h-8 rounded-full text-xs font-display tracking-widest uppercase border transition-colors ${
                  tag === t ? 'bg-[#e89b47] text-[#0a1628] border-[#e89b47]' : 'text-[#d4c4a8] border-[#d4c4a8]/20 hover:border-[#e89b47]'
                }`}
                data-testid={`blog-tag-${t}`}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Posts */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        {loading && (
          <div className="flex items-center justify-center py-16 text-[#d4c4a8]/60" data-testid="blog-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-3" />
            <span className="font-body">Loading posts&hellip;</span>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-16 text-[#d4c4a8]/60 font-body" data-testid="blog-error">
            {error}
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="text-center py-16" data-testid="blog-empty">
            {isFiltering ? (
              <p className="font-body text-[#d4c4a8]/60 mb-4">No posts match your search.</p>
            ) : (
              <p className="font-body text-[#d4c4a8]/60 mb-4">No blog posts yet. Come back soon.</p>
            )}
            <a
              href="https://www.linkedin.com/build-relation/newsletter-follow?entityUrn=7364897628880269312"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#e89b47] hover:underline font-display tracking-wide text-sm"
            >
              Follow on LinkedIn <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}

        {!loading && !error && posts.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
            {posts.map((p) => (
              <article
                key={p.slug}
                className="group rounded-2xl overflow-hidden bg-[#1e2a4a]/40 border border-[#d4c4a8]/10 hover:border-[#e89b47]/40 transition-all hover:shadow-lg hover:shadow-[#e89b47]/5"
                data-testid={`blog-card-${p.slug}`}
              >
                <button
                  type="button"
                  onClick={() => navigate(`/blog/${p.slug}`)}
                  className="block text-left w-full"
                >
                  <div className="aspect-[16/10] bg-[#0f1f35] overflow-hidden">
                    <CoverImage
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <p className="font-body text-xs text-[#e89b47] tracking-wider uppercase mb-2">
                      {formatDate(p.published_at)}
                    </p>
                    <h3 className="font-display text-lg text-[#e8dcc8] leading-snug mb-3 group-hover:text-[#e89b47] transition-colors">
                      {p.title}
                    </h3>
                    {p.excerpt && (
                      <p className="font-body text-sm text-[#d4c4a8]/70 leading-relaxed line-clamp-3">
                        {p.excerpt}
                      </p>
                    )}
                    {(p.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {p.tags.map((t) => (
                          <span key={t} className="px-2 py-0.5 rounded-full text-[10px] font-display tracking-wider uppercase text-[#e89b47]/80 border border-[#e89b47]/25">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <span className="mt-4 inline-flex items-center gap-1.5 font-display text-xs text-[#e89b47] tracking-wider uppercase">
                      Read more <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <SiteFooter />
    </div>
  );
};

export default BlogPage;
